﻿/* global BABYLON */
import { Align, EngineRegistry } from '../DrawingEngine.js';
import { DrawingEngine2020 } from '../DrawingEngine2020.js';
import { BabylonDrawingService } from './BabylonDrawingService.js';
import { RoomAlign, Room } from './BabylonRoom.js';
import { Utils } from '../../main/ObjectUtils.js';

/**
 * The BabylonDrawingEngine is responsible for creating product drawings using the Babylon.js WebGL library.
 */
export class BabylonDrawingEngine extends DrawingEngine2020 {
  constructor(name, options) {
    super(name, options);
    this.assignConfig(options.config);

    this.usesStageProduct = false;
    this.canDoFullScreen = true;

    /* Notes, this.productDrawing should be a <canvas> element. If adaptToDeviceRatio = true, then
     * the canvas will be rendered at a higher resolution; if the window.deviceToPixelRatio = 2,
     * then engine._hardwareScalingLevel will be set to 1/2 = 0.5. If this scaling level = 0.5, then
     * the canvas' width will be doubled (divided by 0.5). So, adaptToDeviceRatio = true and
     * deviceToPixelRatio > 1 leads to higher resolution (canvas.width > canvas.clientWidth). */
    this.engine = new BABYLON.Engine(this.productDrawing, /*antialias:*/ true, /*options:*/ null,
      /*adaptToDeviceRatio:*/ true);

    // Create a basic BJS Scene object.
    this.scene = new BABYLON.Scene(this.engine);
    this.scene.name = name; // For debugging
    // Make the scene transparent:
    this.scene.clearColor = null; // BABYLON.Color3.Red();
    //this.scene.ambientColor = new BABYLON.Color3(0.3, 0.3, 0.3);
    //this.scene.hoverCursor = 'pointer'; // This is the default

    this.createCamera();
    this.createLights();

    //this.scene.debugLayer.show();

    //// Create a built-in "sphere" shape.
    //this.sphere = BABYLON.MeshBuilder.CreateSphere('sphere', { segments: 16, diameter: 2 }, this.scene);
    //// Move the sphere upward 1/2 of its height.
    //this.sphere.position.y = 1;
    // Create a built-in "ground" shape.
    //this.ground = BABYLON.MeshBuilder.CreateGround('ground1', { height: 6, width: 6, subdivisions: 2 }, this.scene);

    //**** DEBUG: Improve visual quality of the scene */

    //const pp = new BABYLON.SharpenPostProcess("sharpen", 1, this.camera);
    //pp.samples = 8;
    //pp.edgeAmount = .3;

    /* These next 2 lines might improve the display of mesh baskets. 4/6/2023.
     * After more fine tuning of how to configure the transparent mesh pattern texture/material
     * it looks like we don't need to make any further optimizations here. See the
     * BabyloneRenderVisitor.lookupMaterial method, the code inside the "if compMatr.hasAlpha"
     * statement for details. 4/18/2023. */
    //this.engine.setHardwareScalingLevel(.5);
    /* TODO: The next line (called "FXAA", antialiasing) causes a bug (probably in our code) where
     * the entire scene isn't fully redrawn after rotating/moving the product around. 4/6/2023. */
    //new BABYLON.FxaaPostProcess("fxaa", 2, this.camera); // Can also try 1 instead of 2?
    // Note, if we enable the previous line, then also try to turn off antialiasing in the engine constructor higher up
    // to see if that improves rendering (particularly on mesh baskets).
    // TODO: Try this, called "FSAA 4X antialiasing" (from demo at https://www.babylonjs.com/demos/csg/):
    //let fx = new BABYLON.PassPostProcess("fsaa", 2, this.camera);
    //fx.renderTargetSamplingMode = BABYLON.Texture.BILINEAR_SAMPLINGMODE;

    /**** END DEBUG */
  }

  assignConfig(config) {
    const defaultAlpha = 1.0;
    this.config = config || {};
    // Default configuration (fallback):
    this.config.bgColor = this.config.bgColor || 'white';
    if (this.config.displayRoom === undefined) {
      this.config.displayRoom = true;
    }
    if (this.config.displayRoom && !this.config.room) {
      this.config.room = {
        width: 2500,
        height: 3000,
        depth: 2000,

        autoWidth: true,
        leftGap: 0,
        rightGap: 500,

        autoDepth: true,
        frontGap: 500,
        backGap: 0,

        autoHeight: true,
        bottomGap: 0,
        topGap: 500,

        align: {
          x: 'left',
          y: 'bottom',
          z: 'back'
        },

        floorColor: '#E2E4DA',
        floorAlpha: defaultAlpha,

        wallColor: 'white',
        wallAlpha: defaultAlpha,
      };
    }
    if (!this.config.camera) {
      this.config.camera = {
        horizDegree: 18, // closet degrees --> about -1.25 alpha (same as 5.0 alpha)
        vertDegree: 4, // --> closet degrees about 1.5 beta
        distance: 3000, // mm
      };
    }
    if (!this.config.hemiLight) {
      this.config.hemiLight = {
        x: -0.6, // Direction from the light source
        y: -0.25,
        z: 0.75,
        intensity: 1.0,
      };
    }
    if (!this.config.dirLight) {
      this.config.dirLight = {
        x: 0.5,
        y: 1.0,
        z: -0.5,
        intensity: 0.3,
      };
    }
  }

  createCamera() {
    //const useUniversalCamera = false;
    //if (useUniversalCamera) {
    //  // Create a FreeCamera, and set its position to (x:0, y:5, z:-10).
    //  const cam = this.camera = new BABYLON.UniversalCamera(this.name + ' Camera', new BABYLON.Vector3(0, 0, -1000), this.scene);
    //  cam.inputs.attached.mouse.touchEnabled = true; // Bug work around - makes touch events work on mobile devices
    //  cam.minZ = 1;
    //  cam.maxZ = 10000;
    //  // Target the camera to scene origin.
    //  cam.setTarget(BABYLON.Vector3.Zero());
    //  cam.inputs.addMouseWheel();
    //  cam.inputs.attached.mousewheel.wheelPrecisionY = 50;
    //  cam.angularSensibility = 5000;
    //  //cam.inputs.addPointers();
    //  //cam.touchAngularSensibility;
    //  //cam.touchMoveSensibility;
    //  cam.attachControl(false);

    //  /* If I wanted to make my own camera input implementation, this is a good start:
    //   * https://doc.babylonjs.com/divingDeeper/cameras/customizingCameraInputs */
    //}

    // Orig: -1.33, 1.33, 3000
    // Testing: -1.25, 1.5, 3000
    const config = this.config.camera;
    //console.log(`-1.25 alpha = ${ClosetDegrees.FromAlphaRadians(-1.25)}`);
    //console.log(`1.5 beta = ${ClosetDegrees.FromBetaRadians(1.5)}`);
    const alpha = ClosetDegrees.ToAlphaRadians(config.horizDegree);
    const beta = ClosetDegrees.ToBetaRadians(config.vertDegree);
    const cam = this.camera = new BABYLON.ArcRotateCamera(this.name + ' Camera', alpha, beta,
      config.distance, new BABYLON.Vector3.Zero(), this.scene);
    cam.wheelDeltaPercentage = 0.01;
    cam.pinchDeltaPercentage = 0.001;
    cam.minZ = 1;
    cam.maxZ = 10000;
    cam.lowerRadiusLimit = 500;
    cam.upperRadiusLimit = 5000;
    /* When camera is in the PI * 3 / 2 position we are looking towards the positive Z axis. At our
      * lower limit (PI) we are looking towards the positive X axis. When we are at the upper
      * limit (PI * 2) we are looking towards the negative X-axis. */
    //cam.lowerAlphaLimit = -Math.PI;
    //cam.upperAlphaLimit = 0;
    cam.lowerAlphaLimit = Math.PI;
    cam.upperAlphaLimit = Math.PI * 2;
    cam.lowerBetaLimit = 0;
    cam.upperBetaLimit = Math.PI;
    //cam.panningDistanceLimit = 1000;
    cam.inputs.attached.keyboard.panningSensibility = 0.3;
    cam.inputs.attached.pointers.panningSensibility = 1;
    //// Allows the camera to automatically position itself relative to the target mesh.
    //cam.useFramingBehavior = true;
    //// This must also be done by the BabylonRenderVisitor (along with the useFramingBehavior above):
    ////cam.setTarget(box);
    //if (cam.behaviors.length > 0) {
    //  const beh = cam.behaviors[0];
    //  beh.framingTime = 500; // 0.5 second vs 1.6 second by default
    //  beh.autoCorrectCameraLimitsAndSensibility = false;
    //}
    cam.useAutoRotationBehavior = false;

    //this.observer = this.scene.onPointerObservable.add(
    //  (info, state) => {
    //    if (info.event.type != 'pointermove') {
    //      console.log(`Scene ${info.event.type} event!!!`);
    //    };
    //  },
    //  BABYLON.PointerEventTypes.POINTERDOWN | BABYLON.PointerEventTypes.POINTERUP |
    //  BABYLON.PointerEventTypes.POINTERMOVE | BABYLON.PointerEventTypes.MOUSEDOWN );

    cam.attachControl(false);

    // Prevent the owl carousel and other listeners of the touchstart event from doing so:
    this.productDrawing.addEventListener('touchstart', evnt => {
      //console.log('ProductDrawing touchstart, preventing and stopping propagation!');
      //evnt.preventDefault(); // Babylon does this
      evnt.stopPropagation(); // But doesn't do this; we need to do it ourselves!
    });
  }

  createLights() {
    const hemiLightCfg = this.config.hemiLight;
    if (hemiLightCfg) {
      /* The hemispheric light was created with the idea that there are two light sources, one from
       * the sky (HemisphericLight.diffuse color) and one from the ground (HemisphericLight.groundColor).
       * Your meshes are in between the sky and the ground and the vertices facing the sky will get
       * the sky color and the vertices facing the ground will get the ground color. The given
       * direction is the direction to the sky (if the sky is right above, the direction would be 0,1,0).
       * This means that the sky light will shine in the opposite direction, and the ground light will
       * shine (if not black) in the same direction. */

      const skyDirection = new BABYLON.Vector3(hemiLightCfg.x, hemiLightCfg.y, hemiLightCfg.z).negateInPlace();
      this.hemiLight = new BABYLON.HemisphericLight('Light (hemispheric)', skyDirection, this.scene);
      this.hemiLight.intensity = hemiLightCfg.intensity;
    }

    // this.frontLight = new BABYLON.DirectionalLight('Front Light (directional)',
    //     new BABYLON.Vector3(0.5, -1, 0.5), this.scene);
    // this.frontLight.intensity = 1.75;

    const dirLightCfg = this.config.dirLight;
    if (dirLightCfg) {
      this.dirLight = new BABYLON.DirectionalLight('Light (directional)',
        new BABYLON.Vector3(dirLightCfg.x, dirLightCfg.y, dirLightCfg.z), this.scene);
      this.dirLight.intensity = dirLightCfg.intensity;
    }
  }

  static canShareLoader() { return true; }

  createDrawingService() {
    return new BabylonDrawingService(this.productDrawing, this.loader.loadedMaterials,
      this.getFinishName, this.scene,
      // Give the service a call back to use if it needs to load a single material just-in-time:
      async (material, loadHighResAsWell, onHisResImgLoaded) => {
        return await this.loadSingleMaterial(material, loadHighResAsWell, onHisResImgLoaded);
      });
  }

  getMaterialImagesFolder() {
    return this.finishImagesBaseUrl;
  }

  toggleDebugView(on) {
    if (on) {
      this.scene.debugLayer.show();
    } else {
      this.scene.debugLayer.hide();
    }
  }

  toggleFullScreen() {
    this.engine.switchFullscreen();
  }

  /** Returns true if the mesh is a descendant of the ancestor (mesh). */
  // Note, this method is not currently in use (see commented code in toggleDisplayObjectInteractive).
  //isDescendant(ancestor, mesh) {
  //  if (!ancestor || !mesh) return false;
  //  if (mesh === ancestor) return true;
  //  return this.isDescendant(ancestor, mesh.parent);
  //}

  /**
   * Turns interaction on/off for the displayObject.
   * @param {displayObject} displayObject The object whose interaction is toggled.
   * @param {bool} on Whether to turn interaction on or off.
   * @param {callback} clickHandler Handler for the displayObject's click event.
   */
  toggleDisplayObjectInteractive(displayObject, on, clickHandler) {
    //const status = on ? 'on' : 'off';
    if (displayObject) {
      if (on) {
        if (!displayObject.actionManager) {
          displayObject.actionManager = new BABYLON.ActionManager(this.scene);
          /* Setting isRecursive to true enables events to "reach" the actions registered on this
           * displayObject. If set to false, only direct hits on the displayObject would execute the
           * action. 1/2/2021: since we now set mesh.isClickable = false by default, we don't have to
           * set isRecursive to true here. Warning: we'll have to check later what happens if a submesh
           * extends outside the bounds of the parent, is it still clickable? */
          //displayObject.actionManager.isRecursive = true;
          //console.log(`%cToggle ${displayObject.name} Interaction ${status}: assigned new displayObject.actionManager`, 'color: orange');
        }
        //console.log(`%cToggle ${displayObject.name} Interaction ${status}: created new clickAction`, 'color: orange');
        this.clickAction = new BABYLON.ExecuteCodeAction(
          {
            trigger: BABYLON.ActionManager.OnPickTrigger,
          },
          event => {
            /* Only respond to left-click events. We could've used OnLeftPickTrigger instead, but
             * that trigger ended up interfering with the built-in mouse events for the
             * ArcRotateCamera, probably because OnLeftPickTrigger is raise on button down
             * (vs button up). 1/1/2021. */
            if (event.sourceEvent.button == 0) { // 0 -> Left button
              /* Event handlers expect an event where target is the object which was clicked, and
               * currentTarget is the object who has the attached event handler. And, both of these
               * objects should have a dataComponent property assigned to the appropriate component. */
              //console.log(`%cToggle ${displayObject.name} Interaction ${status}: received click event`, 'color: orange');
              clickHandler({
                //target: event.meshUnderPointer, // This is the same as scene.meshUnderPointer
                target: event.source, // This is the mesh which was picked
                //currentTarget: event.source, // This is the mesh which was picked, but...
                // ... we need to assign the mesh which we attached this event to instead:
                currentTarget: displayObject,
              });
            }
          });
        displayObject.actionManager.registerAction(this.clickAction);
        displayObject.isPickable = true;

        /* There's a flaw in Babylon ActionManager. If you set isRecursive to true, then the action
         * also is triggered on submeshes. But, the mouse pointer is not updated to use the hoverCursor
         * when over the submeshes. The purpose of the code below is to set the mouse pointer in those
         * cases. This code can be removed if Babylon fixes the issue in the future. 1/1/2021.
         * 1/2/2021: since we now set mesh.isClickable = false by default, we don't have to set
         * isRecursive to true above, and that means we also don't need this code below to set the
         * cursor. */
        //const cursor = displayObject.actionManager.hoverCursor || this.scene.hoverCursor;
        //const canvasStyle = this.productDrawing.style;
        //let priorMesh = null;
        //this.scene.onPointerMove = (evt, pickResult) => {
        //  if (pickResult.hit) {
        //    const mesh = pickResult.pickedMesh;
        //    if (mesh && mesh !== displayObject) { // If mesh == displayObject then default handling is good enough
        //      let isDescendant = mesh === priorMesh; // Performance optimization
        //      if (!isDescendant) {
        //        isDescendant = this.isDescendant(displayObject, mesh);
        //      }
        //      if (isDescendant) {
        //        canvasStyle.cursor = cursor;
        //        priorMesh = mesh;
        //      }
        //    }
        //  }
        //}

      } else {
        if (displayObject.actionManager) {
          //console.log(`%cToggle ${displayObject.name} Interaction ${status}: unregistering the clickAction`, 'color: orange');
          displayObject.actionManager.unregisterAction(this.clickAction);
          this.clickAction = null;
        } else {
          //console.log(`%cToggle ${displayObject.name} Interaction ${status}: the displayObject did not have an actionManager`, 'color: orange');
        }
        displayObject.isPickable = false;
        //this.scene.onPointerMove = null; // Remove the event we installed above (line is now commented because the code above also is commented)
      }

      displayObject.interactive = on; // Our custom attribute
    } else {
      //console.log(`%cToggle Interaction ${status}: displayObject was unassigned!`, 'color: red');
    }
  }

  /**
   * The owner class (e.g. ProductDrawing) can call this method to get the display object to be
   * associated with the stage product. The stage product is a code created root product instance
   * and the parent of the product being displayed/edited.
   */
  getStageDisplayObject() {
    // We don't need a stage display object
    return null;
  }

  /** Needed if the user want's to move the html element into a full screen display. */
  getRootHtmlElement(rootDisplayObject) {
    return this.productDrawing; // This is the <canvas> element
  }

  zoomInOnTarget(target) {
    const cam = this.scene.activeCamera;
    if (cam /*&& cam.setTarget*/) {
      //if (!cam.useFramingBehavior) {
      //  // Allows the camera to automatically position itself relative to the target mesh.
      //  cam.useFramingBehavior = true;
      //  // This must also be done by the BabylonRenderVisitor (along with the useFramingBehavior above):
      //  //cam.setTarget(target);
      //  if (cam.behaviors.length > 0) {
      //    const beh = cam.behaviors[0];
      //    beh.framingTime = 500; // 0.5 second vs 1.6 second by default
      //    beh.autoCorrectCameraLimitsAndSensibility = false;
      //  }
      //}
      //cam.setTarget(target, true);

      //setTimeout(() => {
      //  cam.zoomOn(null, /*doNotUpdateMaxZ:*/ true);
      //}, 100);
      target.computeWorldMatrix();
      cam.zoomOn([target], /*doNotUpdateMaxZ:*/ true);
      // The zoomOn method seems to put the camera a bit too close. Let's add some more distance:
      cam.radius *= 1.2;
      //cam.target.y = -200;
      // Maybe alternatively try out this: https://forum.babylonjs.com/t/frameing-all-objects-into-view-isinfrustum-isoccluded-not-updating-as-expected/4098/4
    }
  }

  getHexColorStr(color) {
    if (color && color[0] === '#') {
      return color;
    }
    return Utils.cssColorStr(color);
  }

  addRoom() {
    if (this.config.displayRoom) {
      const config = this.config.room;
      const room = new Room(this.scene);
      room.width = config.width;
      room.height = config.height;
      room.depth = config.depth;

      room.autoWidth = config.autoWidth;
      room.leftGap = config.leftGap; // Gap between left wall and left edge of content
      room.rightGap = config.rightGap;
      room.align.x = RoomAlign[config.align.x];

      room.autoDepth = config.autoDepth;
      room.backGap = config.backGap; // Gap between back wall and back edge of content
      room.frontGap = config.frontGap;
      room.align.z = RoomAlign[config.align.z];

      room.autoHeight = config.autoHeight;
      room.topGap = config.topGap; // Gap between back wall and back edge of content
      room.bottomGap = config.bottomGap;
      room.align.y = RoomAlign[config.align.z];

      room.floorColor = this.getHexColorStr(config.floorColor);
      room.floorAlpha = config.floorAlpha;
      //room.floorMaterial = drawing.createFloorMaterial();

      room.wallColor = this.getHexColorStr(config.wallColor);
      room.wallAlpha = config.wallAlpha;

      room.includeBaseboards = false;
      room.baseboardColor = '#ffffff';
      room.baseboardAlpha = 1.0;
      return room;
    }
  }

  /**
   * Is responsible for adding a display object to the drawing. Typically this only needs to happen
   * for the root display object, and with some engine this is already done by the fact that the
   * display objects were added to the drawing when they were instantiated (Pixi, Babylon). These
   * subclasses may therefore implement this method with a do-nothing statement.
   * @param {any} productDrawing The <canvas> or <div> element hosting the drawing
   * @param {any} displayObject The html element, mesh or sprite representing the (root) product component
   */
  addToDrawing(displayObject) {
    this.productDrawing.style.backgroundColor = this.config.bgColor;
    const includeRoom = true; // TODO: Move to config
    if (includeRoom) {
      if (!this.room) {
        this.room = this.addRoom();
      }
      if (this.room) {
        this.room.content = displayObject;
        this.room.rebuildAndAddToDrawing();
      }
    }
    this.zoomInOnTarget(displayObject);
  }

  stopShowingFps() {
    if (this.intervalHandle > 0) {
      clearInterval(this.intervalHandle);
      console.log(`${this.name}: Cleared the FPS interval for handle ${this.intervalHandle}`);
      this.intervalHandle = 0;
    }
  }

  showFpsOnConsole(interval, expiration) {
    this.stopShowingFps();
    const lastTime = new Date().getTime();
    this.intervalHandle = window.setInterval(() => {
      let fps;
      if (!this.isRendering()) {
        fps = 0;
      } else {
        fps = this.engine.getFps();
        if (this.isSkippingEvery2nd) {
          fps = fps / 2;
        }
      }
      console.log(`${this.name}: ${fps.toFixed()} FPS`);
      const curTime = new Date().getTime();
      if ((curTime - lastTime).toFixed() > expiration) {
        this.stopShowingFps();
      }
    }, interval);
    console.log(`${this.name}: Set the FPS interval, handle = ${this.intervalHandle}`);
  }

  startRendering() {
    if (!this.isRendering()) {
      //this.showFpsOnConsole(2000, 100000);
      this.isSkippingEvery2nd = true;
      let renderNow = true;
      this.engine.runRenderLoop(() => {
        // We simply halfing the FPS here by skipping every 2nd iteration in the loop
        // (without letting the Babylon engine now about it.)
        if (renderNow) {
          this.scene.render();
        }
        renderNow = !renderNow;
      });
      this._isRendering = true;
      console.log(`${this.name}: Started render loop`);
      return true;
    }
    return false;
  }

  stopRendering() {
    if (this.isRendering()) {
      this.engine.stopRenderLoop();
      this._isRendering = false;
      //this.stopShowingFps();
      console.log(`${this.name}: Stopped render loop`);
      return true;
    }
    return false;
  }

  isRendering() {
    return this._isRendering;
  }

  async renderOnce() {
    if (!this.isRendering()) {
      await this.scene.whenReadyAsync();
      this.scene.render();
      console.log(`${this.name}: Rendered the drawing`);
      //this.startRendering();
    }
  }

  toggleRendering() {
    if (this.isRendering()) {
      return this.stopRendering();
    } else {
      return this.startRendering();
    }
  }

  resizeCanvas(width, height) {
    this.productDrawing.width = width;
    this.productDrawing.height = height;
  }

  resizeCanvasInsideElement(element, alsoUpdateClientSize) {
    this.resizeCanvas(element.clientWidth, element.clientHeight);
    if (alsoUpdateClientSize) {
      /* Remember the canvas width/height can be larger than the clientWidth/-Height (style.width/height).
       * E.g. this will be the case on high dpi devices (is dependent on the owner calling the
       * Babylon engine.resize() method after this call completes). */
      this.productDrawing.style.width = '100%';
      this.productDrawing.style.height = '100%';
    }
  }

  syncCanvasSizeWithParent(alsoUpdateClientSize) {
    this.resizeCanvasInsideElement(this.productDrawing.parentNode, alsoUpdateClientSize);
  }

  resizeCanvasToFitAroundStage(renderOnce) {
    // TODO: Reimplement this:
    // 1. Move root mesh to the top-left corner:
    //this.stage.position.set(0, 0);
    // 2. Set the canvas size to the size of the root mesh:
    //this.resizeCanvas(this.stage.width, this.stage.height);
    //TODO: What about the canvas' clientWidth/-Height (see resizeCanvasInsideElement)
    if (renderOnce) {
      this.renderOnce();
    }
  }

  scaleStageToFitInCanvas(xAlign, yAlign, keepXYOffsets) {
    // TODO: Reimplement this:
    //this.stage.scale.set(1, 1);
    //this.stage.position.set(0, 0);
    //const stageRect = this.stage.getBounds();
    //const stageSize = {
    //  width: stageRect.width,
    //  height: stageRect.height
    //};
    //const canvasSize = this.renderer.screen;
    //const newStageSize = PixiDrawingEngine.scaleUpToContainerSize(stageSize, canvasSize);
    //this.stage.scale.set(newStageSize.width / stageSize.width, newStageSize.height / stageSize.height);
    //let { x, y } = this.stage.getBounds();
    //this.alignStage(newStageSize, canvasSize, xAlign, yAlign);
    //if (!keepXYOffsets) {
    //  this.stage.x -= x;
    //  this.stage.y -= y;
    //}
  }

  bestFitStageAndCanvas(fitCanvasAroundStage, renderOnce) {
    //this.logDisplayObjectGraph(this.stage);
    this.syncCanvasSizeWithParent(/*alsoUpdateClientSize:*/ true);
    /* resize() will partially do the same thing as the line above, but will also take device PixelRatio
     * into consideration. Note, resize() is also called from within this.engine's constructor. */
    this.engine.resize();
    /* TODO: Causes flicker on initial load? Doesn't seem to always happen, but if we can reproduce it,
     * try to put the resize call inside a scene.registerBeforeRender() callback.
     * https://forum.babylonjs.com/t/pointer-on-iphone/1876/7 */
    this.scaleStageToFitInCanvas(Align.center, Align.center);
    if (fitCanvasAroundStage) {
      this.resizeCanvasToFitAroundStage(false);
    }
    //console.log('Performed best fit of stage and canvas');
    if (renderOnce) {
      this.renderOnce();
    }
  }

  resizeCanvasToFitInFullWindow(renderOnce) {
    this.resizeCanvas(window.innerWidth * 0.9, window.innerHeight * 0.9);
    this.scaleStageToFitInCanvas(Align.center, Align.center);
    if (renderOnce) {
      this.renderOnce();
    }
    console.log('resizeCanvasToFitInFullWindow');
  }

  /**
   * Returns an object with the width and height of the drawing. This should match the size of the
   * image returned by the getDrawingImageUrl method.
   */
  getDrawingSize() {
    return {
      width: this.productDrawing.width,
      height: this.productDrawing.height,
    };
  }

  getDrawingImageUrl() {
    // TODO: Consider passing 'image/jpg' to force jpg instead of png (which is better quality, smaller size?)
    //return this.view.toDataURL();
    // NOTE: Here's a good thread on how to save the canvas as an image: https://forum.babylonjs.com/t/best-way-to-save-to-jpeg-snapshots-of-scene/17663/23
    return this.productDrawing.toDataURL();
  }
}

EngineRegistry.register('BabylonDrawingEngine', BabylonDrawingEngine);

/**
 * This is the class we're testing. The purpose is to convert camera angles from 360 units where 0 degrees is
 * right in front of the closet (called "closet degrees") to radians used to set a camera's alpha and beta angles.
 */
class ClosetDegrees {
  /**
   * Converts a camera angle from -90 to +90 units where 0 degrees is right in front of the closet (called "closet degrees")
   * to radians used to set a camera's beta angle.
   */
  static ToBetaRadians(closetDegree) {
    let degree = (90 - closetDegree);
    return BABYLON.Tools.ToRadians(degree);
  }

  /**
   * Converts from a camera's beta angle to vertical closet degrees (-90 to +90 units where 0 degrees is right in front
   * of the closet).
   */
  static FromBetaRadians(radians) {
    const degree = BABYLON.Tools.ToDegrees(radians);
    return 90 - degree;
  }

  /**
   * Converts a camera angle from 360 units where 0 degrees is right in front of the closet (called "closet degrees")
   * to radians used to set a camera's alpha angle.
   */
  static ToAlphaRadians(closetDegree) {
    let degree = (closetDegree + 270) % 360;
    return BABYLON.Tools.ToRadians(degree);
  }

  /**
   * Converts from a camera's alpha angle to horizontal closet degrees (360 units where 0 degrees is right in front
   * of the closet).
   */
  static FromAlphaRadians(radians) {
    const degree = BABYLON.Tools.ToDegrees(radians);
    return (degree + 90) % 360;
  }
}
